import { useState } from 'react';
import EventPopup from './EventPopup';
import type { Event } from '../types/Event';

interface PaginatedEventPopupProps {
  events: Event[];
  isLoggedIn: boolean;
  isModOrAdmin?: boolean;
  onSave: (updatedEvent: Event) => Promise<void>;
  onDelete: (id: string, scope?: 'instance' | 'series') => void;
  onOpenImagePreview?: (imageUrl: string, title: string) => void;
  onReportCountChange?: (eventId: string, count: number) => void;
}

const navBtnStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '28px',
  height: '28px',
  minWidth: '28px',
  borderRadius: '50%',
  backgroundColor: '#ea580c',
  border: '2px solid #ffffff',
  color: '#ffffff',
  cursor: 'pointer',
  fontSize: '18px',
  lineHeight: 1,
  padding: 0,
  flexShrink: 0,
};

export default function PaginatedEventPopup({
  events,
  isLoggedIn,
  isModOrAdmin,
  onSave,
  onDelete,
  onOpenImagePreview,
  onReportCountChange,
}: PaginatedEventPopupProps) {
  const [index, setIndex] = useState(0);
  const total = events.length;
  const safeIndex = Math.min(index, total - 1);
  const event = events[safeIndex];

  const handleDelete = (id: string, scope?: 'instance' | 'series') => {
    if (safeIndex > 0) {
      setIndex(safeIndex - 1);
    }
    if (scope) {
      onDelete(id, scope);
    } else {
      onDelete(id);
    }
  };

  return (
    <div>
      {total > 1 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '10px',
            gap: '8px',
          }}
        >
          <button
            type="button"
            onClick={() => setIndex((safeIndex - 1 + total) % total)}
            aria-label="Poprzednie wydarzenie"
            style={navBtnStyle}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'rgba(11,1,54,0.5)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#ea580c'; }}
          >
            <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true" fill="none">
              <path d="M7 1.5L3 5l4 3.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>

          <span
            style={{
              flex: 1,
              textAlign: 'center',
              fontSize: '11px',
              color: '#cbd5e1',
              fontFamily: '"Helvetica Neue", Arial, Helvetica, sans-serif',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={event.title}
          >
            {safeIndex + 1} / {total} — {event.title}
          </span>

          <button
            type="button"
            onClick={() => setIndex((safeIndex + 1) % total)}
            aria-label="Następne wydarzenie"
            style={navBtnStyle}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'rgba(11,1,54,0.5)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#ea580c'; }}
          >
            <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true" fill="none">
              <path d="M3 1.5L7 5l-4 3.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      )}

      <EventPopup
        key={event.id}
        event={event}
        isLoggedIn={isLoggedIn}
        isModOrAdmin={isModOrAdmin}
        onSave={onSave}
        onDelete={handleDelete}
        onOpenImagePreview={onOpenImagePreview}
        onReportCountChange={onReportCountChange}
      />
    </div>
  );
}
